import { Button } from 'antd';
import Table, { ColumnsType, TableProps } from 'antd/es/table';
import { SortOrder } from 'antd/es/table/interface';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { generatePath, Link } from 'react-router';

import { AdminUserListStreamSortBy } from 'protocol/api/core/svc_adminka_pb';

import { PATHS } from '@/shared/config';
import { useAppSelector } from '@/shared/hooks';

import { usersModel, useUserListStream } from '@/entities/users';

import { useCountryName } from '@/features/countries';

import styles from './UsersTab.module.scss';

type UsersTableDataItem = {
  id: string;
  key: string;
  email: string;
  country: string;
  created: string;
};

type UsersTabProps = {
  debouncedSearch: string;
};

const useUsersTableData = (): UsersTableDataItem[] => {
  const { formatDate } = useIntl();
  const users = useAppSelector(usersModel.selectors.selectAll);
  const getCountryName = useCountryName();

  return useMemo(
    () =>
      users.map((user): UsersTableDataItem => {
        const country = getCountryName(user.Settings.Country);

        return {
          id: user.ID,
          key: user.ID,
          email: user.PersonalData.Emails[0],
          created: formatDate(
            new Date(Number(user?.Created?.At?.seconds) * 1000),
            {
              year: 'numeric',
              month: 'long',
              day: '2-digit',
            },
          ),
          country,
        };
      }),
    [users, getCountryName, formatDate],
  );
};

const useSortUsersTable = () => {
  const [sortOrder, setSortOrder] = useState<SortOrder>();
  const [sortBy, setSortBy] = useState<AdminUserListStreamSortBy>();

  const handleTableSortChange: TableProps<UsersTableDataItem>['onChange'] = (
    pagination,
    filters,
    sorter,
  ) => {
    const currentSorter = Array.isArray(sorter) ? sorter[0] : sorter;

    if (currentSorter.field === 'email') {
      setSortBy(AdminUserListStreamSortBy.AdminUserListStreamSortByEmail);
      setSortOrder(currentSorter.order);
    }

    if (currentSorter.field === undefined) {
      setSortBy(undefined);
      setSortOrder(undefined);
    }
  };

  return {
    sortBy,
    sortOrder,
    handleTableSortChange,
  };
};

export const UsersTab: FC<UsersTabProps> = (props) => {
  const { debouncedSearch } = props;

  const users = useAppSelector(usersModel.selectors.selectAll);
  const loading = useAppSelector(usersModel.selectors.selectLoading);

  const { openUserListStream, closeUserListStream } = useUserListStream();
  const usersTableData = useUsersTableData();
  const { sortBy, sortOrder, handleTableSortChange } = useSortUsersTable();

  const lastUserID = useMemo(() => users.at(-1)?.ID, [users]);

  const handleShowMore = useCallback(() => {
    closeUserListStream(false);

    openUserListStream({
      startFromUserID: lastUserID,
      search: debouncedSearch,
      sortBy,
      sortOrder,
    });
  }, [
    closeUserListStream,
    openUserListStream,
    debouncedSearch,
    lastUserID,
    sortBy,
    sortOrder,
  ]);

  const columns: ColumnsType<UsersTableDataItem> = [
    {
      key: 'email',
      title: <FormattedMessage id="users.email" defaultMessage="E-mail" />,
      dataIndex: 'email',
      sorter: true,
      width: 260,
      className: styles.wrapCell,
    },
    {
      key: 'country',
      title: <FormattedMessage id="users.country" defaultMessage="Country" />,
      dataIndex: 'country',
      width: 20,
      className: styles.wrapCell,
    },
    {
      key: 'created',
      title: <FormattedMessage id="global.created" defaultMessage="Created" />,
      dataIndex: 'created',
      width: 20,
    },
    {
      title: <FormattedMessage id="users.action" defaultMessage="Action" />,
      key: 'action',
      width: 10,
      render: (_, record) => (
        <Link
          className={styles.profileLink}
          to={generatePath(PATHS.user, { userID: record.id })}
        >
          <FormattedMessage id="users.profileLink" defaultMessage="Profile" />
        </Link>
      ),
    },
  ];

  useEffect(() => {
    openUserListStream({
      search: debouncedSearch,
      sortBy,
      sortOrder,
    });

    return () => {
      closeUserListStream();
    };
  }, [debouncedSearch, sortBy, sortOrder]);

  return (
    <div className={styles.container}>
      <Table
        columns={columns}
        dataSource={usersTableData}
        loading={loading}
        pagination={false}
        onChange={handleTableSortChange}
      />

      <Button
        type="text"
        onClick={handleShowMore}
        className={styles.showMoreButton}
      >
        <FormattedMessage id="users.showMore" defaultMessage="Show more" />
      </Button>
    </div>
  );
};
